import { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { adminApi } from '../../../core/api/rtk-query/admin.api';
import { isuGroupApi } from '../../../core/api/rtk-query/isu-group.api';
import 'rc-slider/assets/index.css';
import Slider from 'rc-slider';
// no direct import needed

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-bottom: 20px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.06);
  animation: ${fadeIn} 0.4s ease;
`;

const Chart = styled.div`
  background: #fff;
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.06);
  animation: ${fadeIn} 0.4s ease;
`;

export const AdminDashboardPage = () => {
  const [windowDays, setWindowDays] = useState(0);
  const { data } = adminApi.useGetMetricsQuery(windowDays);
  const [range, setRange] = useState<[number, number]>([0, 30]);

  const { data: isuGroups } = isuGroupApi.useGetIsuGroupsQuery();

  const cards = data?.cards;
  const fullSeries = data?.activity ?? [];
  const fullRegs = data?.registrations ?? [];

  const roleCounts = useMemo(() => {
    const list = (cards?.byRole || []) as any[];
    const idxToName: Record<number, string> = { 0: 'Student', 1: 'Mentor', 2: 'Teacher', 3: 'Admin' };
    const out: Record<string, number> = { Student: 0, Mentor: 0, Teacher: 0, Admin: 0 };
    list.forEach(r => {
      const keyRaw = (r.role ?? r.Role);
      const name = typeof keyRaw === 'number' ? (idxToName[keyRaw] || String(keyRaw)) : String(keyRaw || '');
      const count = r.count ?? r.Count ?? 0;
      if (out[name as keyof typeof out] !== undefined) out[name as keyof typeof out] += count;
    });
    return out;
  }, [cards?.byRole]);

  const aggregate = (arr: any[], maxBars: number, pick: (x: any) => number) => {
    const n = arr.length;
    const from = range[0], to = range[1];
    if (n === 0 || to - from <= 0) return [] as any[];
    const bars = Math.min(maxBars, Math.max(1, to - from));
    const step = Math.max(1, Math.ceil((to - from) / bars));
    const out: { label: string; value: number }[] = [];
    for (let i = from; i < to; i += step) {
      let sum = 0; let label = '';
      for (let j = i; j < Math.min(i + step, to); j++) {
        const item = arr[j];
        sum += item ? (pick(item) || 0) : 0;
        label = item?.day?.toString?.() ?? `${j}`;
      }
      out.push({ label, value: sum });
    }
    return out;
  };

  const bucketedRegs = useMemo(() => aggregate(fullRegs, 30, (x: any) => x.count), [fullRegs, range]);
  const bucketedStarts = useMemo(() => aggregate(fullSeries, 30, (x: any) => x.starts), [fullSeries, range]);
  const bucketedFinishes = useMemo(() => aggregate(fullSeries, 30, (x: any) => x.finishes), [fullSeries, range]);

  const makeYAxis = (values: number[]) => {
    const max = Math.max(1, ...values);
    const step = Math.ceil(max / 4);
    const ticks = [0, step, step * 2, step * 3, step * 4];
    return { max, ticks };
  };

  const yRegs = useMemo(() => makeYAxis(bucketedRegs.map((b:any)=> b.value)), [bucketedRegs]);
  const yAct = useMemo(() => makeYAxis([
    ...bucketedStarts.map((b:any)=> b.value),
    ...bucketedFinishes.map((b:any)=> b.value),
  ]), [bucketedStarts, bucketedFinishes]);

  const xLabels = (arr: any[]) => {
    const n = arr.length;
    const every = n > 12 ? Math.ceil(n / 12) : 1;
    return arr.map((b, i) => (i % every === 0 ? String(b.label).slice(5, 10) : ''));
  };
  const xRegs = useMemo(()=> xLabels(bucketedRegs), [bucketedRegs]);
  const xAct = useMemo(()=> xLabels(bucketedStarts), [bucketedStarts]);

  useEffect(() => {
    const len = Math.max(fullSeries.length, fullRegs.length);
    if (windowDays === 0) {
      setRange([0, Math.max(1, len)]);
    } else {
      setRange([0, windowDays]);
    }
  }, [windowDays, fullSeries.length, fullRegs.length]);

  const sliderMax = Math.max(1, windowDays === 0 ? Math.max(fullSeries.length, fullRegs.length) : windowDays);
  const getIndexLabel = (i:number) => {
    const raw = fullSeries[i]?.day || fullRegs[i]?.day || ''+i;
    // Ожидаем ISO-даты
    const s = String(raw);
    if (s.length >= 10 && s.includes('-')) return s.slice(0,10);
    return s;
  };
  const leftLabel = getIndexLabel(Math.max(0, Math.min(sliderMax-1, range[0])));
  const rightLabel = getIndexLabel(Math.max(0, Math.min(sliderMax-1, range[1]-1)));
  const edgeLeft = getIndexLabel(0);
  const edgeRight = getIndexLabel(sliderMax-1);

  // Активность по дням недели (Mon..Sun)
  const weekdayStats = useMemo(() => {
    const totals = Array(7).fill(0);
    const start = Math.max(0, range[0]);
    const end = Math.max(start, range[1]);
    for (let i = start; i < end; i++) {
      const dStr = fullSeries[i]?.day;
      if (!dStr) continue;
      const d = new Date(dStr);
      const dow = d.getDay(); // 0..6 (Sun..Sat)
      const idx = (dow + 6) % 7; // 0=Mon .. 6=Sun
      totals[idx] += fullSeries[i]?.starts || 0;
    }
    return totals;
  }, [fullSeries, range]);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns:'auto 1fr', gap: 12, alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Дашборд</h2>
        <div style={{ display:'grid', gridTemplateColumns:'auto 1fr', gap:12, alignItems:'center' }}>
          <select value={windowDays} onChange={e => setWindowDays(Number(e.target.value))} style={{ padding:'8px 12px', border:'1px solid #e5e8f0', borderRadius:12, background:'#fff' }}>
            <option value={0}>за всё время</option>
            <option value={7}>7 дней</option>
            <option value={30}>30 дней</option>
            <option value={180}>180 дней</option>
            <option value={365}>365 дней</option>
          </select>
          <div style={{ width:'80%', marginLeft:'auto' }}>
            <div style={{ position:'relative', padding:'32px 6px 28px', borderRadius:10 }}>
              {/* Края над слайдером */}
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#667', position:'absolute', left:6, right:6, top:6 }}>
                <span>{edgeLeft}</span>
                <span>{edgeRight}</span>
              </div>
              <div style={{ position:'absolute', left:6, right:6, top:18, height:2, background:'#e5e8f0' }} />
              <Slider
                range
                min={0}
                max={sliderMax}
                value={range}
                onChange={(v:any)=> setRange(v)}
                styles={{
                  rail: { backgroundColor:'transparent' },
                  track: { backgroundColor:'#4F7DF3', height:2, top:18 },
                  handle: { backgroundColor:'#4F7DF3', borderColor:'#4F7DF3' },
                } as any}
              />
              {/* Подвижные метки под бегунками */}
              {leftLabel !== edgeLeft && (
                <span style={{ position:'absolute', top:28, left:`calc(${(range[0]/sliderMax)*100}% + 6px)`, transform:'translateX(-50%)', fontSize:12, color:'#667' }}>{leftLabel}</span>
              )}
              {rightLabel !== edgeRight && (
                <span style={{ position:'absolute', top:28, left:`calc(${((Math.max(0, range[1]-1))/sliderMax)*100}% + 6px)`, transform:'translateX(-50%)', fontSize:12, color:'#667' }}>{rightLabel}</span>
              )}
            </div>
          </div>
        </div>
      </div>

       <Grid>
        <Card>
          <div>Всего пользователей</div>
          <h3>{cards?.totalUsers ?? '—'}</h3>
        </Card>
        <Card>
          <div>Не верифицированы</div>
          <h3>{cards?.notVerified ?? '—'}</h3>
        </Card>
        <Card>
          <div>По ролям</div>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, marginTop:6}}>
            <div style={{display:'flex', justifyContent:'space-between'}}>
              <span>Student</span><b>{roleCounts.Student}</b>
            </div>
            <div style={{display:'flex', justifyContent:'space-between'}}>
              <span>Mentor</span><b>{roleCounts.Mentor}</b>
            </div>
            <div style={{display:'flex', justifyContent:'space-between'}}>
              <span>Teacher</span><b>{roleCounts.Teacher}</b>
            </div>
            <div style={{display:'flex', justifyContent:'space-between'}}>
              <span>Admin</span><b>{roleCounts.Admin}</b>
            </div>
          </div>
        </Card>
        <Card>
          <div>Группы</div>
          <h3>{cards?.totalGroups ?? 0}</h3>
        </Card>
        <Card>
          <div>Мероприятия</div>
          <h3>{cards?.totalEvents ?? 0}</h3>
        </Card>
      </Grid>

      <div style={{display:'grid', gridTemplateColumns:'1.5fr 1fr', gap:16}}>
        <Chart>
          <div style={{ marginBottom: 8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span>Регистрации пользователей</span>
            <span style={{fontSize:12, color:'#778'}}>период: {windowDays === 0 ? Math.max(fullSeries.length, fullRegs.length) : windowDays} дн.</span>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'40px 1fr', gap:8 }}>
            <div style={{ display:'flex', flexDirection:'column', justifyContent:'space-between', height:200, color:'#667', fontSize:12 }}>
              {yRegs.ticks.slice().reverse().map((t, i)=>(<div key={i} style={{textAlign:'right'}}>{t}</div>))}
            </div>
            <div>
              <div style={{ position:'relative', height:200 }}>
                {yRegs.ticks.map((t,i)=>(
                  <div key={i} style={{ position:'absolute', left:0, right:0, top:`${(1 - (t / yRegs.ticks[yRegs.ticks.length-1]))*100}%`, borderTop:'1px dashed #eef1f6' }} />
                ))}
                <div style={{ display:'flex', gap:6, alignItems:'end', position:'absolute', left:0, right:0, bottom:0 }}>
                  {bucketedRegs.map((b:any, idx:number) => (
                    <div key={idx} style={{ flex: 1 }}>
                      <div title={`${b.label}: ${b.value}`} style={{ height: yRegs.max ? Math.max(2, Math.min(180, (b.value / yRegs.max) * 180)) : 2, background: '#4F7DF3', borderRadius: 6 }} />
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display:'flex', gap:6, color:'#667', fontSize:10, marginTop:6 }}>
                {xRegs.map((lab, i)=>(<div key={i} style={{ flex:1, textAlign:'center' }}>{lab}</div>))}
              </div>
            </div>
          </div>
        </Chart>
        <Chart>
          <div style={{ marginBottom: 8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span>Активность по мероприятиям</span>
            <span style={{fontSize:12, color:'#778'}}>единицы: кол-во</span>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'40px 1fr', gap:8 }}>
            <div style={{ display:'flex', flexDirection:'column', justifyContent:'space-between', height:200, color:'#667', fontSize:12 }}>
              {yAct.ticks.slice().reverse().map((t, i)=>(<div key={i} style={{textAlign:'right'}}>{t}</div>))}
            </div>
            <div>
              <div style={{ position:'relative', height:200, overflow:'hidden' }}>
                {yAct.ticks.map((t,i)=>(
                  <div key={i} style={{ position:'absolute', left:0, right:0, top:`${(1 - (t / yAct.ticks[yAct.ticks.length-1]))*100}%`, borderTop:'1px dashed #eef1f6' }} />
                ))}
                <div style={{ display:'flex', gap:2, alignItems:'end', position:'absolute', left:0, right:0, bottom:0, overflow:'hidden' }}>
                  {bucketedStarts.map((b:any, idx:number) => (
                    <div key={idx} style={{ flex: 1, display:'flex', alignItems:'end', gap:2, padding:'0 1px' }}>
                      <div title={`Старт ${b.label}: ${b.value}`} style={{ width:5, height: yAct.max ? Math.max(2, Math.min(180, (b.value / yAct.max) * 180)) : 2, background: '#0b68fe', borderRadius: 3 }} />
                      <div title={`Финиш ${bucketedFinishes[idx]?.label||''}: ${bucketedFinishes[idx]?.value||0}`} style={{ width:5, height: yAct.max ? Math.max(2, Math.min(180, ((bucketedFinishes[idx]?.value || 0) / yAct.max) * 180)) : 2, background: '#00C853', borderRadius: 3 }} />
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display:'flex', gap:6, color:'#667', fontSize:10, marginTop:6 }}>
                {xAct.map((lab, i)=>(<div key={i} style={{ flex:1, textAlign:'center' }}>{lab}</div>))}
              </div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#777', marginTop:6 }}>Синие — старты, зелёные — закрытия</div>
        </Chart>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
        <Card>
          <div style={{marginBottom:8, fontWeight:700}}>Активность по дням недели</div>
          <div style={{ display:'flex', gap:8, alignItems:'end', height:160 }}>
            {['Пн','Вт','Ср','Чт','Пт','Сб','Вс'].map((label, idx)=> {
              const max = Math.max(1, ...weekdayStats);
              const val = weekdayStats[idx] || 0;
              return (
                <div key={label} style={{ flex:1, textAlign:'center' }}>
                  <div style={{ height: Math.max(2, (val/max)*140), background:'#7e9cff', borderRadius:6 }} />
                  <div style={{ fontSize:12, color:'#667', marginTop:6 }}>{label}</div>
                </div>
              );
            })}
          </div>
        </Card>
        <Card>
          <div style={{marginBottom:8, fontWeight:700}}>ТОП групп ИСУ (по числу пользователей)</div>
          <div style={{ display:'grid', gap:6 }}>
            {((isuGroups||[]).slice().sort((a:any,b:any)=> (b.count||0)-(a.count||0)).slice(0,10)).map((g:any)=> {
              const max = Math.max(1, ...((isuGroups||[]).map((x:any)=> x.count||0)));
              return (
                <div key={g.name} style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ width:120, color:'#667', fontSize:12, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{g.name||'—'}</div>
                  <div style={{ flex:1, height:10, background:'#eef1f6', borderRadius:999 }}>
                    <div style={{ width:`${(g.count/max)*100}%`, height:'100%', background:'#4F7DF3', borderRadius:999 }} />
                  </div>
                  <div style={{ width:36, textAlign:'right', fontSize:12 }}>{g.count}</div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
};


